import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { LatLng, LatLngTuple } from 'leaflet';
import { DeviceNodeType, DeviceTracking } from '@/api/apis/composer';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';
import { hereMapsQueries } from '@/queries/here-maps';
import { decodePolyline } from '../utils/polyline.utils';

type GroundRouteRequest = {
  nodeId: string;
  startLocation: LatLng;
  endLocation: LatLng;
  departureTime?: string;
};

const buildRequests = (data: DeviceTracking | undefined): GroundRouteRequest[] => {
  if (!data?.deviceNodes?.length) return [];

  return data.deviceNodes.flatMap((node) => {
    if (node.nodeType !== DeviceNodeType.Ground) return [];
    if (node.plannedPolyline?.polyline || node.trackedPolyline?.polyline) return [];

    const dep = node.departureCheckpoint?.coordinate;
    const arr = node.arrivalCheckpoint?.coordinate;
    if (dep?.latitude == null || dep?.longitude == null) return [];
    if (arr?.latitude == null || arr?.longitude == null) return [];

    const departureTime = node.departureTime?.atd ?? node.departureTime?.ptd ?? node.departureTime?.std ?? undefined;

    return [
      {
        nodeId: node.nodeId,
        startLocation: new LatLng(dep.latitude, dep.longitude),
        endLocation: new LatLng(arr.latitude, arr.longitude),
        departureTime,
      },
    ];
  });
};

export const useGroundLegRoutes = (data: DeviceTracking | undefined): Map<string, LatLngTuple[]> => {
  const requests = useMemo(() => buildRequests(data), [data]);

  const results = useQueries({
    queries: requests.map((req) =>
      hereMapsQueries.getGroundRoute({
        startLocation: req.startLocation,
        endLocation: req.endLocation,
        type: CalculateGroundTimeType.DepartureTime,
        timestamp: req.departureTime,
      }),
    ),
  });

  return useMemo(() => {
    const map = new Map<string, LatLngTuple[]>();
    requests.forEach((req, i) => {
      const res = results[i]?.data;
      if (!res?.isFeasible || !res.polylines.length) return;
      const positions = res.polylines.flatMap((p) => decodePolyline(p));
      if (positions.length > 1) map.set(req.nodeId, positions);
    });
    return map;
    // TODO: fix dependency array eslint error
    // eslint-disable-next-line @tanstack/query/no-unstable-deps
  }, [requests, results]);
};
